"use client";

import * as React from "react";
import {
  IconArrowAutofitDown,
  IconArrowAutofitLeft,
  IconArrowAutofitRight,
  IconArrowAutofitUp,
  IconColumnInsertLeft,
  IconColumnInsertRight,
  IconCopy,
  IconGripHorizontal,
  IconGripVertical,
  IconPlus,
  IconRowInsertBottom,
  IconRowInsertTop,
  IconTrash,
} from "@tabler/icons-react";
import {
  COL_RESIZE_HIT_PX,
  ROW_RESIZE_HIT_PX,
  TABLE_AXIS_MENU_GAP_PX,
  TABLE_AXIS_MENU_MIN_WIDTH_PX,
} from "./constants";
import { clampAxisMenuLeft, clampAxisMenuTop } from "./menu-anchor-utils";
import type { AddStripLayoutRect } from "./table-add-strip-visibility";
import type {
  ColumnResizeGuide,
  LineDragVisual,
  RowResizeGuide,
  TableAxisMenuAnchor,
  TableCtx,
} from "./types";

/** 与 `SlashMenuPlugin` 中斜杠菜单面板/行一致：边框、背景、悬停、圆角、阴影、字号 */
const AXIS_MENU_PANEL_CLASS =
  "fixed z-[240] flex min-w-48 w-48 max-h-80 flex-col overflow-y-auto rounded-lg border border-divider bg-surface py-1 text-sm text-surface-foreground shadow-lg outline-none";
const AXIS_MENU_ITEM_CLASS =
  "flex w-full shrink-0 cursor-default items-center gap-2 px-3 py-2 text-left text-sm text-surface-foreground outline-none hover:bg-surface-secondary disabled:pointer-events-none disabled:opacity-40";
const AXIS_MENU_DANGER_ITEM_CLASS =
  "flex w-full shrink-0 cursor-default items-center gap-2 px-3 py-2 text-left text-sm text-danger outline-none hover:bg-danger/10 disabled:pointer-events-none disabled:opacity-40";
const AXIS_MENU_RULE_CLASS = "my-1 shrink-0 border-t border-divider";

export type TableInteractionPortalsProps = {
  lineDragVisual: LineDragVisual | null;
  rowLineDragGhostMountRef: React.RefObject<HTMLDivElement | null>;
  colLineDragGhostMountRef: React.RefObject<HTMLDivElement | null>;
  axisFocusOverlayStyle: React.CSSProperties | null;
  rowMenuOpen: boolean;
  colMenuOpen: boolean;
  closeAllAxisMenusRef: React.MutableRefObject<() => void>;
  ctx: TableCtx | null;
  showHandles: boolean;
  rowGripStyle: React.CSSProperties | null;
  colGripStyle: React.CSSProperties | null;
  columnResizeGuides: ColumnResizeGuide[];
  rowResizeGuides: RowResizeGuide[];
  resizingColIndex: number | null;
  resizingRowIndex: number | null;
  onResizePointerDown: (tableKey: string, colIndex: number, e: React.PointerEvent) => void;
  onResizePointerMove: (e: React.PointerEvent) => void;
  onResizePointerUp: (e: React.PointerEvent) => void;
  onResizeLostPointerCapture: () => void;
  onRowResizePointerDown: (tableKey: string, rowIndex: number, e: React.PointerEvent) => void;
  onRowResizePointerMove: (e: React.PointerEvent) => void;
  onRowResizePointerUp: (e: React.PointerEvent) => void;
  onRowResizeLostPointerCapture: () => void;
  insertRowAtEnd: () => void;
  insertColAtEnd: () => void;
  rowMenuAnchor: TableAxisMenuAnchor | null;
  colMenuAnchor: TableAxisMenuAnchor | null;
  closeRowAxisMenu: () => void;
  closeColAxisMenu: () => void;
  onRowInsertAbove: () => void;
  onRowInsertBelow: () => void;
  onRowCopy: () => void;
  onRowMoveUp: () => void;
  onRowMoveDown: () => void;
  onRowDelete: () => void;
  onColInsertLeft: () => void;
  onColInsertRight: () => void;
  onColCopy: () => void;
  onColMoveLeft: () => void;
  onColMoveRight: () => void;
  onColDelete: () => void;
  onRowGripPointerDown: (e: React.PointerEvent) => void;
  onRowGripPointerMove: (e: React.PointerEvent) => void;
  onRowGripPointerUp: (e: React.PointerEvent) => void;
  onColGripPointerDown: (e: React.PointerEvent) => void;
  onColGripPointerMove: (e: React.PointerEvent) => void;
  onColGripPointerUp: (e: React.PointerEvent) => void;
  onRowGripLostPointerCapture: () => void;
  onColGripLostPointerCapture: () => void;
  suppressRowClickRef: React.MutableRefObject<boolean>;
  suppressColClickRef: React.MutableRefObject<boolean>;
  rowAddStripLayout: AddStripLayoutRect | null;
  colAddStripLayout: AddStripLayoutRect | null;
};

export function TableInteractionPortals({
  lineDragVisual,
  rowLineDragGhostMountRef,
  colLineDragGhostMountRef,
  axisFocusOverlayStyle,
  rowMenuOpen,
  colMenuOpen,
  closeAllAxisMenusRef,
  ctx,
  showHandles,
  rowGripStyle,
  colGripStyle,
  columnResizeGuides,
  rowResizeGuides,
  resizingColIndex,
  resizingRowIndex,
  onResizePointerDown,
  onResizePointerMove,
  onResizePointerUp,
  onResizeLostPointerCapture,
  onRowResizePointerDown,
  onRowResizePointerMove,
  onRowResizePointerUp,
  onRowResizeLostPointerCapture,
  insertRowAtEnd,
  insertColAtEnd,
  rowMenuAnchor,
  colMenuAnchor,
  closeRowAxisMenu,
  closeColAxisMenu,
  onRowInsertAbove,
  onRowInsertBelow,
  onRowCopy,
  onRowMoveUp,
  onRowMoveDown,
  onRowDelete,
  onColInsertLeft,
  onColInsertRight,
  onColCopy,
  onColMoveLeft,
  onColMoveRight,
  onColDelete,
  onRowGripPointerDown,
  onRowGripPointerMove,
  onRowGripPointerUp,
  onColGripPointerDown,
  onColGripPointerMove,
  onColGripPointerUp,
  onRowGripLostPointerCapture,
  onColGripLostPointerCapture,
  suppressRowClickRef,
  suppressColClickRef,
  rowAddStripLayout,
  colAddStripLayout,
}: TableInteractionPortalsProps) {
  return (
    <>
      {lineDragVisual?.kind === "row" && (
        <div
          className="pointer-events-none box-border overflow-hidden rounded-md border-2 border-accent bg-surface/60 shadow-lg backdrop-blur-md"
          style={{
            position: "fixed",
            left: lineDragVisual.table.left,
            top: lineDragVisual.row.top + lineDragVisual.offsetY,
            width: lineDragVisual.table.width,
            height: lineDragVisual.row.height,
            zIndex: 230,
            boxSizing: "border-box",
          }}
          aria-hidden
        >
          <div ref={rowLineDragGhostMountRef} className="h-full min-h-0 w-full" />
        </div>
      )}
      {lineDragVisual?.kind === "col" && (
        <div
          className="pointer-events-none box-border overflow-hidden rounded-md border-2 border-accent bg-surface/60 shadow-lg backdrop-blur-md"
          style={{
            position: "fixed",
            left: lineDragVisual.col.left + lineDragVisual.offsetX,
            top: lineDragVisual.table.top,
            width: lineDragVisual.col.width,
            height: lineDragVisual.table.height,
            zIndex: 230,
            boxSizing: "border-box",
          }}
          aria-hidden
        >
          <div ref={colLineDragGhostMountRef} className="h-full min-h-0 w-full" />
        </div>
      )}

      {lineDragVisual?.kind === "row" && lineDragVisual.dropLine && (
        <div
          className="pointer-events-none rounded-none bg-accent"
          style={{
            position: "fixed",
            left: lineDragVisual.dropLine.left,
            top: lineDragVisual.dropLine.top,
            width: lineDragVisual.dropLine.width,
            height: lineDragVisual.dropLine.height,
            zIndex: 232,
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      )}
      {lineDragVisual?.kind === "col" && lineDragVisual.dropLine && (
        <div
          className="pointer-events-none rounded-none bg-accent"
          style={{
            position: "fixed",
            left: lineDragVisual.dropLine.left,
            top: lineDragVisual.dropLine.top,
            width: lineDragVisual.dropLine.width,
            height: lineDragVisual.dropLine.height,
            zIndex: 232,
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      )}

      {axisFocusOverlayStyle && (
        <div
          className="pointer-events-none box-border border-2 border-accent"
          style={axisFocusOverlayStyle}
          aria-hidden
        />
      )}

      {(rowMenuOpen || colMenuOpen) && (
        <div
          aria-hidden
          className="fixed inset-0 z-[238]"
          onPointerDown={() => closeAllAxisMenusRef.current()}
        />
      )}

      {ctx && rowGripStyle && showHandles && (
        <div className="flex overflow-hidden rounded-sm border border-divider bg-content1 shadow-sm" style={rowGripStyle}>
          <button
            type="button"
            title="行：拖拽调序，单击打开菜单"
            aria-label="行：拖拽调序，单击打开菜单"
            className={
              lineDragVisual?.kind === "row"
                ? "flex h-full w-full cursor-grabbing items-center justify-center border-0 bg-transparent text-default-500 outline-none"
                : "flex h-full w-full cursor-grab items-center justify-center border-0 bg-transparent text-default-500 outline-none active:cursor-grabbing"
            }
            onPointerDown={onRowGripPointerDown}
            onPointerMove={onRowGripPointerMove}
            onPointerUp={onRowGripPointerUp}
            onPointerCancel={onRowGripPointerUp}
            onLostPointerCapture={onRowGripLostPointerCapture}
            onClick={(e) => {
              if (suppressRowClickRef.current) {
                suppressRowClickRef.current = false;
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <IconGripVertical size={11} stroke={1.5} />
          </button>
        </div>
      )}

      {rowMenuOpen && rowMenuAnchor && ctx && (
        <div
          role="menu"
          aria-label="行操作"
          className={AXIS_MENU_PANEL_CLASS}
          style={{
            top: clampAxisMenuTop(rowMenuAnchor.bottom + TABLE_AXIS_MENU_GAP_PX, 260),
            left: clampAxisMenuLeft(rowMenuAnchor.left),
            minWidth: TABLE_AXIS_MENU_MIN_WIDTH_PX,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onRowInsertAbove();
              closeRowAxisMenu();
            }}
          >
            <IconRowInsertTop size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>上方插入行</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onRowInsertBelow();
              closeRowAxisMenu();
            }}
          >
            <IconRowInsertBottom size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>下方插入行</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onRowCopy();
              closeRowAxisMenu();
            }}
          >
            <IconCopy size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>复制行</span>
          </button>
          <div className={AXIS_MENU_RULE_CLASS} role="separator" />
          <button
            type="button"
            role="menuitem"
            disabled={!ctx.simple || ctx.rowIndex <= 0}
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onRowMoveUp();
              closeRowAxisMenu();
            }}
          >
            <IconArrowAutofitUp size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>上移一行</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!ctx.simple || ctx.rowIndex >= ctx.rowCount - 1}
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onRowMoveDown();
              closeRowAxisMenu();
            }}
          >
            <IconArrowAutofitDown size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>下移一行</span>
          </button>
          <div className={AXIS_MENU_RULE_CLASS} role="separator" />
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_DANGER_ITEM_CLASS}
            onClick={() => {
              onRowDelete();
              closeRowAxisMenu();
            }}
          >
            <IconTrash size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>删除行</span>
          </button>
        </div>
      )}

      {ctx && colGripStyle && showHandles && (
        <div className="flex overflow-hidden rounded-sm border border-divider bg-content1 shadow-sm" style={colGripStyle}>
          <button
            type="button"
            title="列：拖拽调序，单击打开菜单"
            aria-label="列：拖拽调序，单击打开菜单"
            className={
              lineDragVisual?.kind === "col"
                ? "flex h-full w-full cursor-grabbing items-center justify-center border-0 bg-transparent text-default-500 outline-none"
                : "flex h-full w-full cursor-grab items-center justify-center border-0 bg-transparent text-default-500 outline-none active:cursor-grabbing"
            }
            onPointerDown={onColGripPointerDown}
            onPointerMove={onColGripPointerMove}
            onPointerUp={onColGripPointerUp}
            onPointerCancel={onColGripPointerUp}
            onLostPointerCapture={onColGripLostPointerCapture}
            onClick={(e) => {
              if (suppressColClickRef.current) {
                suppressColClickRef.current = false;
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <IconGripHorizontal size={11} stroke={1.5} />
          </button>
        </div>
      )}

      {colMenuOpen && colMenuAnchor && ctx && (
        <div
          role="menu"
          aria-label="列操作"
          className={AXIS_MENU_PANEL_CLASS}
          style={{
            top: clampAxisMenuTop(colMenuAnchor.top, 280),
            left: clampAxisMenuLeft(colMenuAnchor.right + TABLE_AXIS_MENU_GAP_PX),
            minWidth: TABLE_AXIS_MENU_MIN_WIDTH_PX,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onColInsertLeft();
              closeColAxisMenu();
            }}
          >
            <IconColumnInsertLeft size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>左侧插入列</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onColInsertRight();
              closeColAxisMenu();
            }}
          >
            <IconColumnInsertRight size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>右侧插入列</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onColCopy();
              closeColAxisMenu();
            }}
          >
            <IconCopy size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>复制列</span>
          </button>
          <div className={AXIS_MENU_RULE_CLASS} role="separator" />
          <button
            type="button"
            role="menuitem"
            disabled={!ctx.simple || ctx.colIndex <= 0}
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onColMoveLeft();
              closeColAxisMenu();
            }}
          >
            <IconArrowAutofitLeft size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>左移一列</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!ctx.simple || ctx.colIndex >= ctx.colCount - 1}
            className={AXIS_MENU_ITEM_CLASS}
            onClick={() => {
              onColMoveRight();
              closeColAxisMenu();
            }}
          >
            <IconArrowAutofitRight size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>右移一列</span>
          </button>
          <div className={AXIS_MENU_RULE_CLASS} role="separator" />
          <button
            type="button"
            role="menuitem"
            className={AXIS_MENU_DANGER_ITEM_CLASS}
            onClick={() => {
              onColDelete();
              closeColAxisMenu();
            }}
          >
            <IconTrash size={16} stroke={1.5} className="shrink-0 opacity-80" />
            <span>删除列</span>
          </button>
        </div>
      )}

      {columnResizeGuides.map((g) => {
        const lineActive = resizingColIndex === g.colIndex;
        return (
          <div
            key={`rsz-${g.tableKey}-${g.colIndex}`}
            role="separator"
            aria-orientation="vertical"
            title="悬停或拖拽调整列宽"
            data-rt-table-col-resize-hit=""
            data-table-key={g.tableKey}
            className="group rt-table-col-resize-hit fixed z-[227] flex cursor-col-resize justify-center"
            style={{
              top: g.top,
              left: g.centerX - COL_RESIZE_HIT_PX / 2,
              width: COL_RESIZE_HIT_PX,
              height: g.height,
            }}
            onPointerDown={(e) => onResizePointerDown(g.tableKey, g.colIndex, e)}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onPointerCancel={onResizePointerUp}
            onLostPointerCapture={onResizeLostPointerCapture}
          >
            <span
              className={
                lineActive
                  ? "pointer-events-none h-full w-[2px] shrink-0 bg-primary opacity-100 transition-opacity duration-150"
                  : "pointer-events-none h-full w-[2px] shrink-0 bg-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              }
              aria-hidden
            />
          </div>
        );
      })}

      {rowResizeGuides.map((g) => {
        const lineActive = resizingRowIndex === g.rowIndex;
        return (
          <div
            key={`rsz-row-${g.tableKey}-${g.rowIndex}`}
            role="separator"
            aria-orientation="horizontal"
            title="悬停或拖拽调整行高"
            data-rt-table-row-resize-hit=""
            data-table-key={g.tableKey}
            className="group rt-table-row-resize-hit fixed z-[228] flex cursor-row-resize flex-col items-center justify-center"
            style={{
              top: g.centerY - ROW_RESIZE_HIT_PX / 2,
              left: g.left,
              width: g.width,
              height: ROW_RESIZE_HIT_PX,
            }}
            onPointerDown={(e) => onRowResizePointerDown(g.tableKey, g.rowIndex, e)}
            onPointerMove={onRowResizePointerMove}
            onPointerUp={onRowResizePointerUp}
            onPointerCancel={onRowResizePointerUp}
            onLostPointerCapture={onRowResizeLostPointerCapture}
          >
            <span
              className={
                lineActive
                  ? "pointer-events-none h-[2px] w-full shrink-0 bg-primary opacity-100 transition-opacity duration-150"
                  : "pointer-events-none h-[2px] w-full shrink-0 bg-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100"
              }
              aria-hidden
            />
          </div>
        );
      })}

      {rowAddStripLayout && (
        <button
          type="button"
          title="在表格末尾添加一行"
          aria-label="在表格末尾添加一行"
          className="fixed z-[226] flex shrink-0 items-center justify-center rounded-sm border border-border bg-surface text-default-500 shadow-none transition-colors hover:bg-surface-secondary hover:text-default-foreground"
          style={{
            top: rowAddStripLayout.top,
            left: rowAddStripLayout.left,
            width: rowAddStripLayout.width,
            height: rowAddStripLayout.height,
          }}
          onClick={insertRowAtEnd}
        >
          <IconPlus size={14} stroke={1.5} className="shrink-0 opacity-80" />
        </button>
      )}
      {colAddStripLayout && (
        <button
          type="button"
          title="在表格末尾添加一列"
          aria-label="在表格末尾添加一列"
          className="fixed z-[226] flex shrink-0 items-center justify-center rounded-sm border border-border bg-surface text-default-500 shadow-none transition-colors hover:bg-surface-secondary hover:text-default-foreground"
          style={{
            top: colAddStripLayout.top,
            left: colAddStripLayout.left,
            width: colAddStripLayout.width,
            height: colAddStripLayout.height,
          }}
          onClick={insertColAtEnd}
        >
          <IconPlus size={14} stroke={1.5} className="shrink-0 opacity-80" />
        </button>
      )}
    </>
  );
}
